import style from "./style.scss";
import { useState, useRef, useEffect, useCallback } from "preact/hooks";
import { Fragment, lazy, Suspense } from "preact/compat";
import { Button } from "../../../components/link";
import { mergeClasses } from "../../helpers";

const ZoomPanWrapper = lazy(() => import("./zoom-pan-wrapper.js"));

const PopupImage = ({
  images,
  index = 0,
  referrerpolicy = null,
  loading = "lazy",
  showAlt,
  closePopupHandler,
  backgroundColor = null,
  buttonsData = null,
  ariaLabel = null,
}) => {
  const [slideshowIndex, setSlideshowIndex] = useState(index);
  const [currentScale, setCurrentScale] = useState(1);
  const transformRef = useRef(null);
  const imageRef = useRef(null);
  const touchStart = useRef(null);
  const isMultiTouch = useRef(false);

  const zoomIn = () => {
    if (transformRef.current) {
      transformRef.current.zoomIn();
    }
  };

  const zoomOut = () => {
    if (transformRef.current) {
      transformRef.current.zoomOut();
    }
  };

  const resetZoom = useCallback(() => {
    if (transformRef.current) {
      transformRef.current.resetTransform(0);
    }
    setCurrentScale(1);
  }, []);

  const prevClick = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
      }
      const length = images?.length || 0;
      if (length <= 1) return;

      setSlideshowIndex((prev) => (prev <= 0 ? length - 1 : prev - 1));
      resetZoom();
    },
    [images?.length, resetZoom]
  );

  const nextClick = useCallback(
    (e) => {
      if (e) {
        e.preventDefault();
      }
      const length = images?.length || 0;
      if (length <= 1) return;

      setSlideshowIndex((prev) => (prev >= length - 1 ? 0 : prev + 1));
      resetZoom();
    },
    [images?.length, resetZoom]
  );

  // Swipe gestures
  const handleTouchStart = (e) => {
    isMultiTouch.current = e.touches.length > 1;
    touchStart.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  };

  const handleTouchMove = (e) => {
    if (e.touches.length > 1) {
      isMultiTouch.current = true;
    }
  };

  const handleTouchEnd = (e) => {
    if (
      isMultiTouch.current ||
      touchStart.current === null ||
      currentScale > 1.05 // don't allow swipe gestures while zoomed
    ) {
      return;
    }

    const endX = e.changedTouches[0].clientX;
    const endY = e.changedTouches[0].clientY;

    const diffX = touchStart.current.x - endX;
    const diffY = touchStart.current.y - endY;

    touchStart.current = null;

    const horizontalSwipe = {
      min: 50,
      maxDrift: 30,
    };

    const verticalSwipe = {
      min: 80,
      maxDrift: 50,
    };

    // Horizontal swipe (left/right)
    if (
      Math.abs(diffX) > horizontalSwipe.min &&
      Math.abs(diffY) < horizontalSwipe.maxDrift
    ) {
      if (diffX > 0) {
        nextClick();
      } else {
        prevClick();
      }
      return;
    }

    // Vertical swipe (up/down) to close
    if (
      Math.abs(diffY) > verticalSwipe.min &&
      Math.abs(diffX) < verticalSwipe.maxDrift
    ) {
      closePopupHandler();
      return;
    }
  };

  const handleTransform = (ref) => {
    setCurrentScale(ref.state.scale);

    // Desktop/Reset Fix: If scale returns to 1, force absolute center
    if (ref.state.scale <= 1) {
      if (ref.state.positionX !== 0 || ref.state.positionY !== 0) {
        ref.setTransform(0, 0, 1, 0, "linear");
      }
    }
  };

  // Keyboard events
  const handleKeydown = useCallback(
    (e) => {
      switch (e.key) {
        case "Escape":
          closePopupHandler();
          break;
        case "ArrowLeft":
          prevClick();
          break;
        case "ArrowRight":
          nextClick();
          break;
      }
    },
    [closePopupHandler, prevClick, nextClick]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeydown);
    return () => window.removeEventListener("keydown", handleKeydown);
  }, [handleKeydown]);

  const image = images[slideshowIndex];
  if (!image) {
    return null;
  }

  const isMultiImage = images.length > 1;
  const hasAlt = showAlt && image.alt;

  return (
    <div
      class={mergeClasses([style.imagePopup, "dark"])}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel || undefined}
    >
      <div class={style.popupWrapper}>
        <div class={style.header}>
          <button
            class={style.closeButton}
            type="button"
            aria-label="Close dialog"
            onClick={closePopupHandler}
          />
        </div>
        <div
          class={style.zoomViewport}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onClick={(e) => {
            // Only close if the click is on the background area,
            if (imageRef.current && e.target !== imageRef.current) {
              closePopupHandler();
            }
          }}
        >
          <Suspense fallback={<div />}>
            <ZoomPanWrapper
              transformRef={transformRef}
              onTransformed={handleTransform}
            >
              <div
                class={style.fakeHeader}
                aria-hidden="true"
                inert
                tabindex={-1}
              >
                <button class={style.closeButton} type="button" disabled />
              </div>
              <div class={style.imageWrapper}>
                <img
                  ref={imageRef}
                  src={image.lg}
                  alt={image.alt}
                  referrerpolicy={referrerpolicy}
                  loading={loading === "eager" ? "eager" : "lazy"}
                  class={style.mainImage}
                  style={{ backgroundColor: backgroundColor }}
                />
              </div>
              <div
                class={mergeClasses([
                  style.fakeFooter,
                  hasAlt && !isMultiImage ? style.singleWithAlt : "",
                ])}
                aria-hidden="true"
                inert
                tabindex={-1}
              >
                {hasAlt ? (
                  <div class={style.description}>
                    <p>{image.alt}</p>
                  </div>
                ) : null}
                <div class={style.zoom}>
                  <button type="button" disabled />
                  <button type="button" disabled />
                </div>
                {isMultiImage ? (
                  <Fragment>
                    <div class={style.numbers}>
                      {slideshowIndex + 1} / {images.length}
                    </div>
                    <div class={style.pagination}>
                      <button type="button" disabled />
                      <button type="button" disabled />
                    </div>
                  </Fragment>
                ) : buttonsData ? (
                  <ul class={style.linkButtons}>
                    {buttonsData.map((button, index) => (
                      <li key={index}>
                        <Button size="medium" icon={button.icon}>
                          {button.text}
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </ZoomPanWrapper>
          </Suspense>
        </div>
        <div
          class={mergeClasses([
            style.footer,
            hasAlt && !isMultiImage ? style.singleWithAlt : null,
            buttonsData && !isMultiImage ? style.hasButtons : style.noButtons,
          ])}
        >
          {hasAlt ? (
            <div class={style.description}>
              <p>{image.alt}</p>
            </div>
          ) : null}
          <div class={style.zoom}>
            <button
              type="button"
              aria-label="Zoom out"
              onClick={zoomOut}
              disabled={currentScale <= 1}
            >
              −
            </button>
            <button
              type="button"
              aria-label="Zoom in"
              onClick={zoomIn}
              disabled={currentScale >= 6}
            >
              +
            </button>
          </div>
          {isMultiImage ? (
            <Fragment>
              <div
                class={style.numbers}
                aria-label={`Slide ${slideshowIndex + 1} of ${images.length}`}
              >
                {slideshowIndex + 1} / {images.length}
              </div>
              <div class={style.pagination}>
                <button
                  type="button"
                  onClick={(e) => prevClick(e)}
                  aria-label="Previous slide"
                >
                  &lt;
                </button>
                <button
                  type="button"
                  onClick={(e) => nextClick(e)}
                  aria-label="Next slide"
                >
                  &gt;
                </button>
              </div>
            </Fragment>
          ) : buttonsData ? (
            <ul class={style.linkButtons}>
              {buttonsData.map((button, index) => (
                <li key={index}>
                  <Button
                    size="medium"
                    href={button.href}
                    affiliateData={button.affiliateData}
                    affiliate={button.affiliate}
                    icon={button.icon}
                    secondary={button.secondary}
                  >
                    {button.text}
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PopupImage;
